/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import SignalPopup from '../../heart-rate-sessions/SignalPopup';
import { showTimeInSec, formatISODateToLocal } from '../../../utils/utils';
import useTabData from './useTabData';
import { TabLoading, TabError, TabEmpty } from './TabStates';
import { q, Kpi } from './kit';

const LIMIT = 20;
// Matches the /monitor-sessions/:id detail page's device mapping.
const deviceLabel = (v) => (String(v) === 'true' ? 'Watch' : String(v) === 'false' ? 'Mobile' : 'Other');

// Columns mirror the standalone /monitor-sessions/:id detail page so the drill-down lives
// inline in the tab instead of navigating away.
const MonitorSessionsTab = ({ userId, from, to, authGet }) => {
  const [page, setPage] = useState(1);
  const [popup, setPopup] = useState(null); // null = closed; the row's signal payload otherwise
  useEffect(() => { setPage(1); }, [userId, from, to]);

  const { status, data, reload } = useTabData(async () => {
    const res = await authGet(`/admin/api/monitor-sessions?${q(userId, from, to, { page, limit: LIMIT })}`);
    return { rows: res.data.data || [], total: res.data.totalItem || 0, totalPages: res.data.totalPages || 1 };
  }, [userId, from, to, page]);

  if (status === 'loading') return <TabLoading />;
  if (status === 'error') return <TabError onRetry={reload} />;

  const { rows, total, totalPages } = data;
  if (!rows.length && page === 1) {
    return <TabEmpty title="No monitor sessions" message="This user has no monitor sessions in the selected window." icon="📈" />;
  }

  return (
    <div data-testid="ud-monitor">
      <div className="ud-kpis ud-kpis--2">
        <Kpi label="Monitor sessions" value={total} />
      </div>

      <div className="au-panel">
        <div className="au-panel__head"><h4 className="au-panel__title">Sessions</h4></div>
        <div className="au-table-wrap">
          <table className="au-table" data-testid="ud-monitor-table">
            <thead>
              <tr>
                <th>No</th><th>Perform time</th><th>Session time</th><th>Session name</th>
                <th>From Device</th><th>Session Date</th><th>Signal Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id}>
                  <td data-label="No" className="tnum">{(page - 1) * LIMIT + i + 1}</td>
                  <td data-label="Perform time" className="tnum">{showTimeInSec(r.session_perform_time)}</td>
                  <td data-label="Session time" className="tnum">{showTimeInSec(r.session_time)}</td>
                  <td data-label="Session name">{r.session_name || '—'}</td>
                  <td data-label="From Device">{deviceLabel(r.is_from_watch)}</td>
                  <td data-label="Session Date">{formatISODateToLocal(r.timestamp_utc_formatted)}</td>
                  <td data-label="Signal Details">
                    <button
                      type="button"
                      className="au-link"
                      aria-label={`View signal details for record ${r.id}`}
                      onClick={() => setPopup(r.row_hr_details ?? null)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="ud-pager" data-testid="ud-monitor-pager">
            <button type="button" className="au-btn au-btn--ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>← Prev</button>
            <span className="ud-pager__label">Page {page} of {totalPages} · {total} sessions</span>
            <button type="button" className="au-btn au-btn--ghost" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next →</button>
          </div>
        )}
      </div>

      {popup != null && <SignalPopup data={popup} onClose={() => setPopup(null)} />}
    </div>
  );
};

export default MonitorSessionsTab;
